import { directSupabase } from '../../../lib/supabaseClient';
import type { Document } from '../types/documents.types';
import { computeTotals } from '../types/documents.types';

export interface DocumentForProjectEvents {
  id: string;
  lifecycleRef: string;
  kind: 'Estimate' | 'Quotation' | 'Invoice';
  title: string;
  depositAmount: number;
  depositPercentage: number | null;
  hasDeposit: boolean;
  grandTotal: number;
  status: string;
  createdAt: string;
  versionNumber?: number | null;
  invoiceRole?: string | null;
  invoiceRef?: string | null;
}

export const documentsIntegrationService = {
  /**
   * Get all JARVIS documents for a project that can be used in Project Events
   * This provides deposit information for email templates
   */
  async getDocumentsForProjectEvents(projectId: string): Promise<DocumentForProjectEvents[]> {
    const normalizeDeposit = (raw: { has_deposit?: any; deposit_amount?: any; deposit_percentage?: any }) => {
      const amount = Number(raw?.deposit_amount || 0);
      const pct = Number(raw?.deposit_percentage || 0);
      // has_deposit=false must be authoritative. For legacy rows where flag is null/undefined,
      // fall back to numeric fields so older data still works.
      const hasRawFlag = raw?.has_deposit === true ? true : raw?.has_deposit === false ? false : null;
      const hasDeposit = hasRawFlag === null ? (amount > 0 || pct > 0) : hasRawFlag;
      return {
        hasDeposit,
        depositAmount: hasDeposit ? amount : 0,
        depositPercentage: hasDeposit && pct > 0 ? pct : null,
      };
    };

    const { data, error } = await directSupabase
      .from('documents')
      .select(`
        id,
        lifecycle_ref,
        kind,
        status,
        created_at,
        active_version_id,
        organisation_id,
        title,
        deposit_amount,
        deposit_percentage,
        has_deposit,
        admin_pct,
        admin_cap,
        contingency_pct,
        profit_pct,
        vat_enabled,
        vat_pct,
        invoice_role,
        invoice_ref,
        gross_total
      `)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);

    // For each document, get active version data
    const documentsWithActiveData = await Promise.all(
      (data || []).map(async (row) => {
        try {
          if (row.kind === 'Invoice') {
            const { data: docLines, error: lineErr } = await directSupabase
              .from('document_lines')
              .select('line_kind, qty, unit_price, line_total, vat_inclusive')
              .eq('document_id', row.id);

            if (lineErr) {
              console.warn(`Error fetching lines for invoice ${row.id}:`, lineErr);
              return null;
            }

            const mappedLines = (docLines || []).map(line => ({
              lineKind: line.line_kind,
              qty: Number(line.qty ?? 0),
              unitPrice: Number(line.unit_price ?? 0),
              lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
              vatInclusive: !!line.vat_inclusive,
            }));

            const feeCfg = {
              adminPct: Number(row.admin_pct ?? 0),
              adminCap: Number(row.admin_cap ?? 0),
              contingencyPct: Number(row.contingency_pct ?? 0),
              profitPct: Number(row.profit_pct ?? 0),
              developerPct: 0,
            };
            const vatCfg = {
              enabled: !!row.vat_enabled,
              pct: Number(row.vat_pct ?? 0.2),
            };

            const totals = computeTotals(mappedLines, feeCfg, vatCfg);
            const storedGross = Number((row as { gross_total?: number }).gross_total ?? 0);
            const grandTotal = storedGross > 0 ? storedGross : totals.grandTotal;
            const deposit = normalizeDeposit(row);

            return {
              id: row.id,
              lifecycleRef: row.lifecycle_ref,
              kind: row.kind,
              title: row.title || '',
              depositAmount: deposit.depositAmount,
              depositPercentage: deposit.depositPercentage,
              hasDeposit: deposit.hasDeposit,
              grandTotal,
              status: row.status,
              createdAt: row.created_at,
              versionNumber: null,
              invoiceRole: (row as { invoice_role?: string | null }).invoice_role ?? null,
              invoiceRef: (row as { invoice_ref?: string | null }).invoice_ref ?? null,
            };
          } else {
            // For non-invoices, get active version header
            const { data: versionHeader, error: headerErr } = await directSupabase
              .from('document_versions')
              .select('title, deposit_amount, deposit_percentage, has_deposit, admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct, version_number')
              .eq('id', row.active_version_id)
              .single();
            
            if (headerErr) {
              console.warn(`No active version found for document ${row.id}`);
              return null;
            }

            // Get active version lines
            const { data: versionLines, error: lineErr } = await directSupabase
              .from('document_version_lines')
              .select('line_kind, qty, unit_price, line_total, vat_inclusive')
              .eq('version_id', row.active_version_id);

            if (lineErr) {
              console.warn(`Error fetching lines for document ${row.id}:`, lineErr);
              return null;
            }

            const mappedLines = (versionLines || []).map(line => ({
              lineKind: line.line_kind,
              qty: Number(line.qty ?? 0),
              unitPrice: Number(line.unit_price ?? 0),
              lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
              vatInclusive: !!line.vat_inclusive,
            }));

            const feeCfg = {
              adminPct: Number(versionHeader.admin_pct ?? 0),
              adminCap: Number(versionHeader.admin_cap ?? 0),
              contingencyPct: Number(versionHeader.contingency_pct ?? 0),
              profitPct: Number(versionHeader.profit_pct ?? 0),
              developerPct: 0,
            };
            const vatCfg = {
              enabled: !!versionHeader.vat_enabled,
              pct: Number(versionHeader.vat_pct ?? 0.2),
            };

            const totals = computeTotals(mappedLines, feeCfg, vatCfg);
            const grandTotal = totals.grandTotal;
            const deposit = normalizeDeposit(versionHeader);

            return {
              id: row.id,
              lifecycleRef: row.lifecycle_ref,
              kind: row.kind,
              title: versionHeader.title,
              depositAmount: deposit.depositAmount,
              depositPercentage: deposit.depositPercentage,
              hasDeposit: deposit.hasDeposit,
              grandTotal,
              status: row.status,
              createdAt: row.created_at,
              versionNumber: Number((versionHeader as { version_number?: number }).version_number) > 0
                ? Math.trunc(Number((versionHeader as { version_number?: number }).version_number))
                : null,
              invoiceRole: null,
              invoiceRef: null,
            };
          }
        } catch (error) {
          console.warn(`Error processing document ${row.id}:`, error);
          return null;
        }
      })
    );

    // Filter out null results
    return documentsWithActiveData.filter(doc => doc !== null) as DocumentForProjectEvents[];
  },

  /**
   * Get a specific JARVIS document with full details for Project Events
   */
  async getDocumentForProjectEvents(documentId: string): Promise<DocumentForProjectEvents | null> {
    const { data, error } = await directSupabase
      .from('documents')
      .select(`
        id,
        lifecycle_ref,
        invoice_ref,
        kind,
        status,
        created_at,
        active_version_id
      `)
      .eq('id', documentId)
      .eq('source', 'jarvis')
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw new Error(error.message);
    }

    // Get active version header
    const { data: versionHeader, error: headerErr } = await directSupabase
      .from('document_versions')
      .select('title, deposit_amount, deposit_percentage, has_deposit, admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct, version_number')
      .eq('id', data.active_version_id)
      .single();
    
    if (headerErr) {
      console.warn(`No active version found for document ${documentId}`);
      return null;
    }

    // Get active version lines
    const { data: versionLines, error: lineErr } = await directSupabase
      .from('document_version_lines')
      .select('line_kind, qty, unit_price, line_total, vat_inclusive')
      .eq('version_id', data.active_version_id);

    if (lineErr) {
      console.warn(`Error fetching lines for document ${documentId}:`, lineErr);
      return null;
    }

    const mappedLines = (versionLines || []).map(line => ({
      lineKind: line.line_kind,
      qty: Number(line.qty ?? 0),
      unitPrice: Number(line.unit_price ?? 0),
      lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
      vatInclusive: !!line.vat_inclusive,
    }));

    const feeCfg = {
      adminPct: Number(versionHeader.admin_pct ?? 0),
      adminCap: Number(versionHeader.admin_cap ?? 0),
      contingencyPct: Number(versionHeader.contingency_pct ?? 0),
      profitPct: Number(versionHeader.profit_pct ?? 0),
      developerPct: 0,
    };
    const vatCfg = {
      enabled: !!versionHeader.vat_enabled,
      pct: Number(versionHeader.vat_pct ?? 0.2),
    };

    const totals = computeTotals(mappedLines, feeCfg, vatCfg);
    const grandTotal = totals.grandTotal;

    return {
      id: data.id,
      lifecycleRef: data.lifecycle_ref,
      kind: data.kind,
      title: versionHeader.title,
      depositAmount: Number(versionHeader.deposit_amount || 0),
      depositPercentage: versionHeader.deposit_percentage != null ? Number(versionHeader.deposit_percentage) : null,
      hasDeposit: !!versionHeader.has_deposit,
      grandTotal,
      status: data.status,
      createdAt: data.created_at,
      versionNumber: Number((versionHeader as { version_number?: number }).version_number) > 0
        ? Math.trunc(Number((versionHeader as { version_number?: number }).version_number))
        : null,
      invoiceRole: null,
      invoiceRef: (data as { invoice_ref?: string | null }).invoice_ref ?? null,
    };
  },

  /**
   * Get deposit information for a project (compatible with Project Events email templates)
   * This combines both QuoteDock and JARVIS documents
   */
  async getProjectDepositInfo(projectId: string): Promise<{
    jarvisDocuments: DocumentForProjectEvents[];
    hasDeposits: boolean;
    totalDepositAmount: number;
    depositPercentage: number | null;
  }> {
    const jarvisDocuments = await this.getDocumentsForProjectEvents(projectId);
    
    const hasDeposits = jarvisDocuments.some(doc => doc.hasDeposit);
    const totalDepositAmount = jarvisDocuments.reduce((sum, doc) => sum + doc.depositAmount, 0);
    
    // Get the most recent deposit percentage (if any documents have percentage-based deposits)
    const documentsWithPercentage = jarvisDocuments.filter(doc => doc.depositPercentage != null);
    const depositPercentage = documentsWithPercentage.length > 0 
      ? documentsWithPercentage[0].depositPercentage 
      : null;

    return {
      jarvisDocuments,
      hasDeposits,
      totalDepositAmount,
      depositPercentage,
    };
  },

  /**
   * Get the latest invoice for a project (for deposit requests and outstanding payments)
   */
  async getLatestInvoiceForProject(projectId: string): Promise<DocumentForProjectEvents | null> {
    const { data, error } = await directSupabase
      .from('documents')
      .select(`
        id,
        lifecycle_ref,
        kind,
        title,
        deposit_amount,
        deposit_percentage,
        has_deposit,
        status,
        created_at,
        organisation_id,
        admin_pct,
        admin_cap,
        contingency_pct,
        profit_pct,
        vat_enabled,
        vat_pct,
        invoice_role,
        invoice_ref
      `)
      .eq('project_id', projectId)
      .eq('source', 'jarvis')
      .eq('kind', 'Invoice')
      .neq('status', 'void')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw new Error(error.message);
    }

    // Use the same logic as getDocumentsForProjectEvents - get totals from source quotation
    // Find the source quotation for this invoice
    const { data: quote, error: qErr } = await directSupabase
      .from('documents')
      .select('id, active_version_id')
      .eq('organisation_id', data.organisation_id)
      .eq('lifecycle_ref', data.lifecycle_ref)
      .eq('kind', 'Quotation')
      .maybeSingle();
    
    if (qErr || !quote?.active_version_id) {
      console.warn(`No source quotation found for invoice ${data.id}, using base document data`);
      // Fallback to base document data if no quotation found
      const { data: docLines, error: lineErr } = await directSupabase
        .from('document_lines')
        .select('line_kind, qty, unit_price, line_total, vat_inclusive')
        .eq('document_id', data.id);

      if (lineErr) {
        console.warn(`Error fetching lines for invoice ${data.id}:`, lineErr);
        return null;
      }

      const mappedLines = (docLines || []).map(line => ({
        lineKind: line.line_kind,
        qty: Number(line.qty ?? 0),
        unitPrice: Number(line.unit_price ?? 0),
        lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
        vatInclusive: !!line.vat_inclusive,
      }));

      const feeCfg = {
        adminPct: Number(data.admin_pct ?? 0),
        adminCap: Number(data.admin_cap ?? 0),
        contingencyPct: Number(data.contingency_pct ?? 0),
        profitPct: Number(data.profit_pct ?? 0),
        developerPct: 0,
      };
      const vatCfg = {
        enabled: !!data.vat_enabled,
        pct: Number(data.vat_pct ?? 0.2),
      };

      const totals = computeTotals(mappedLines, feeCfg, vatCfg);
      const grandTotal = totals.grandTotal;

      return {
        id: data.id,
        lifecycleRef: data.lifecycle_ref,
        kind: data.kind,
        title: data.title || '',
        depositAmount: Number(data.deposit_amount || 0),
        depositPercentage: data.deposit_percentage != null ? Number(data.deposit_percentage) : null,
        hasDeposit: !!data.has_deposit,
        grandTotal,
        status: data.status,
        createdAt: data.created_at,
        versionNumber: null,
        invoiceRole: (data as { invoice_role?: string | null }).invoice_role ?? null,
        invoiceRef: (data as { invoice_ref?: string | null }).invoice_ref ?? null,
      };
    }

    // Get active version header from source quotation
    const { data: versionHeader, error: headerErr } = await directSupabase
      .from('document_versions')
      .select('title, deposit_amount, deposit_percentage, has_deposit, admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct, version_number')
      .eq('id', quote.active_version_id)
      .single();
    
    if (headerErr) {
      console.warn(`No active version found for source quotation of invoice ${data.id}`);
      return null;
    }

    // Get active version lines from source quotation
    const { data: versionLines, error: lineErr } = await directSupabase
      .from('document_version_lines')
      .select('line_kind, qty, unit_price, line_total, vat_inclusive')
      .eq('version_id', quote.active_version_id);

    if (lineErr) {
      console.warn(`Error fetching lines for source quotation of invoice ${data.id}:`, lineErr);
      return null;
    }

    // Map lines to the format expected by computeTotals
    const mappedLines = (versionLines || []).map(line => ({
      lineKind: line.line_kind,
      qty: Number(line.qty ?? 0),
      unitPrice: Number(line.unit_price ?? 0),
      lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
      vatInclusive: !!line.vat_inclusive,
    }));

    const feeCfg = {
      adminPct: Number(versionHeader.admin_pct ?? 0),
      adminCap: Number(versionHeader.admin_cap ?? 0),
      contingencyPct: Number(versionHeader.contingency_pct ?? 0),
      profitPct: Number(versionHeader.profit_pct ?? 0),
      developerPct: 0,
    };
    const vatCfg = {
      enabled: !!versionHeader.vat_enabled,
      pct: Number(versionHeader.vat_pct ?? 0.2),
    };

    // Use computeTotals for consistent calculation
    const totals = computeTotals(mappedLines, feeCfg, vatCfg);
    const grandTotal = totals.grandTotal;

    return {
      id: data.id,
      lifecycleRef: data.lifecycle_ref,
      kind: data.kind,
      title: versionHeader.title,
      depositAmount: Number(versionHeader.deposit_amount || 0),
      depositPercentage: versionHeader.deposit_percentage != null ? Number(versionHeader.deposit_percentage) : null,
      hasDeposit: !!versionHeader.has_deposit,
      grandTotal,
      status: data.status,
      createdAt: data.created_at,
      versionNumber: Number((versionHeader as { version_number?: number }).version_number) > 0
        ? Math.trunc(Number((versionHeader as { version_number?: number }).version_number))
        : null,
      invoiceRole: (data as { invoice_role?: string | null }).invoice_role ?? null,
      invoiceRef: (data as { invoice_ref?: string | null }).invoice_ref ?? null,
    };
  },

  /**
   * Calculate outstanding balance for a JARVIS document
   * This is used for Project Events email templates
   */
  calculateOutstandingBalance(document: DocumentForProjectEvents): number {
    const role = String(document.invoiceRole || '').trim().toLowerCase();
    if (role === 'deposit') return 0;
    if (role === 'works' || role === 'variation' || role === 'credit') {
      return Math.max(Number(document.grandTotal || 0), 0);
    }
    if (!document.hasDeposit) {
      return document.grandTotal;
    }
    return Math.max(document.grandTotal - document.depositAmount, 0);
  },

  /**
   * Get deposit and outstanding information for email templates
   */
  getDepositInfoForTemplates(document: DocumentForProjectEvents): {
    depositAmount: number;
    depositPercentage: number | null;
    outstandingAmount: number;
    hasDeposit: boolean;
    grandTotal: number;
  } {
    const outstandingAmount = this.calculateOutstandingBalance(document);
    
    return {
      depositAmount: document.depositAmount,
      depositPercentage: document.depositPercentage,
      outstandingAmount,
      hasDeposit: document.hasDeposit,
      grandTotal: document.grandTotal,
    };
  },
};
